import { useState, useMemo } from "react";
import { Badge, Form, Row, Col } from "react-bootstrap";
import moment from "moment";

import Table from "../../common/components/Table";
import Pagination from "../../common/components/Pagination";
import IconButton from "../../common/components/IconButton";
import Modal from "../../common/components/Modal";
import { useAuditLogQuery } from "./hooks/useAuditLogQuery";
import { methodLabel, endpointLabel } from "./labels";
import { BsEye, BsArrowClockwise } from "react-icons/bs";

const METHOD_OPTIONS = [
  { value: "", label: "Todas las acciones" },
  { value: "POST", label: "Crear" },
  { value: "PUT", label: "Actualizar" },
  { value: "PATCH", label: "Modificar" },
  { value: "DELETE", label: "Eliminar" },
];

const methodColor = (method) => {
  switch (method) {
    case "POST":
      return "success";
    case "PUT":
    case "PATCH":
      return "warning";
    case "DELETE":
      return "danger";
    default:
      return "dark";
  }
};

const statusColor = (code) => {
  if (!code) return "dark";
  if (code >= 200 && code < 300) return "success";
  if (code >= 300 && code < 400) return "info";
  if (code >= 400 && code < 500) return "warning";
  return "danger";
};

const formatBody = (body) => {
  if (!body) return "(sin cuerpo)";
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return String(body);
  }
};

const parseDeletedData = (raw) => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// Convierte un valor en texto legible para alguien sin conocimiento técnico:
// - null/undefined/"" -> "(vacío)"
// - true/false        -> "Sí" / "No"
// - fechas ISO        -> "DD/MM/YYYY HH:mm"
// - objetos/arreglos  -> JSON compacto sin comillas innecesarias
const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted">(vacío)</span>;
  }
  if (typeof value === "boolean") {
    return value ? "Sí" : "No";
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (value instanceof Date) {
    return moment(value).format("DD/MM/YYYY HH:mm");
  }
  if (typeof value === "string") {
    // Detectar fechas ISO (ej. "2026-04-30T02:03:39.000Z")
    const isoDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
    if (isoDate) {
      const m = moment(value);
      if (m.isValid()) return m.format("DD/MM/YYYY HH:mm");
    }
    return value;
  }
  if (Array.isArray(value) || typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

function ListAuditLogs() {
  const [filters, setFilters] = useState({
    search: "",
    method: "",
    startDate: "",
    endDate: "",
  });
  const [selectedLog, setSelectedLog] = useState(null);

  const stableFilters = useMemo(
    () => filters,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filters)]
  );

  const { logs, loading, totalPages, page, onChangePage, refresh } =
    useAuditLogQuery(stableFilters);

  const handleFilterChange = (key) => (e) => {
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const columns = [
    {
      header: "Fecha / Hora",
      accessor: "createdAt",
      render: (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm:ss") : "-"),
    },
    {
      header: "Usuario",
      accessor: "userName",
      render: (v, item) => (
        <div>
          <div className="fw-semibold">{v || "(sin autenticar)"}</div>
          {item.userProfile && (
            <small className="text-muted">{item.userProfile}</small>
          )}
        </div>
      ),
    },
    {
      header: "Acción",
      accessor: "method",
      render: (v) => <Badge bg={methodColor(v)}>{methodLabel(v)}</Badge>,
    },
    {
      header: "Módulo",
      accessor: "endpoint",
      render: (v) => (
        <span style={{ wordBreak: "break-word" }}>{endpointLabel(v)}</span>
      ),
    },
    {
      header: "Estado",
      accessor: "statusCode",
      render: (v) => <Badge bg={statusColor(v)}>{v}</Badge>,
    },
    {
      header: "Detalle",
      accessor: "actions",
      render: (_, item) => (
        <IconButton
          tooltipText="Ver detalle"
          onClick={() => setSelectedLog(item)}
        >
          <BsEye size={20} />
        </IconButton>
      ),
    },
  ];

  return (
    <main className="container-fluid p-4">
      <header className="d-flex justify-content-between align-items-center mb-4 px-3 py-2 border-bottom">
        <h1 className="h2 m-0 fw-bold">Logs de auditoría del sistema</h1>
        <IconButton tooltipText="Refrescar" onClick={refresh}>
          <BsArrowClockwise size={24} />
        </IconButton>
      </header>

      <Row className="g-3 mb-3 px-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Buscar (usuario o módulo)</Form.Label>
            <Form.Control
              type="text"
              value={filters.search}
              onChange={handleFilterChange("search")}
              placeholder="ej: Juan, siniestros..."
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Acción</Form.Label>
            <Form.Select
              value={filters.method}
              onChange={handleFilterChange("method")}
            >
              {METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Desde</Form.Label>
            <Form.Control
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange("startDate")}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Hasta</Form.Label>
            <Form.Control
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange("endDate")}
            />
          </Form.Group>
        </Col>
      </Row>

      <section className="shadow w-100 d-flex flex-column">
        <Table columns={columns} data={logs} loading={loading} showIndex />
        <Pagination
          totalPages={totalPages}
          page={page}
          onChangePage={onChangePage}
        />
      </section>

      <Modal isOpen={!!selectedLog} onClose={() => setSelectedLog(null)}>
        {selectedLog && (
          <div className="p-4">
            <h4 className="mb-3">Detalle del log #{selectedLog.id}</h4>
            <dl className="row mb-0">
              <dt className="col-sm-4">Fecha / Hora</dt>
              <dd className="col-sm-8">
                {moment(selectedLog.createdAt).format("DD/MM/YYYY HH:mm:ss")}
              </dd>

              <dt className="col-sm-4">Usuario</dt>
              <dd className="col-sm-8">
                {selectedLog.userName || "(sin autenticar)"} (ID:{" "}
                {selectedLog.userId || "-"})
              </dd>

              <dt className="col-sm-4">Perfil</dt>
              <dd className="col-sm-8">{selectedLog.userProfile || "-"}</dd>

              <dt className="col-sm-4">Acción</dt>
              <dd className="col-sm-8">
                <Badge bg={methodColor(selectedLog.method)}>
                  {methodLabel(selectedLog.method)}
                </Badge>
              </dd>

              <dt className="col-sm-4">Módulo</dt>
              <dd className="col-sm-8">
                {endpointLabel(selectedLog.endpoint)}
                <div>
                  <small className="text-muted" style={{ wordBreak: "break-all" }}>
                    {selectedLog.endpoint}
                  </small>
                </div>
              </dd>

              <dt className="col-sm-4">Estado HTTP</dt>
              <dd className="col-sm-8">
                <Badge bg={statusColor(selectedLog.statusCode)}>
                  {selectedLog.statusCode}
                </Badge>
              </dd>

              <dt className="col-sm-4">User-Agent</dt>
              <dd className="col-sm-8">
                <small style={{ wordBreak: "break-all" }}>
                  {selectedLog.userAgent || "-"}
                </small>
              </dd>

              <dt className="col-sm-4">Datos enviados</dt>
              <dd className="col-sm-8">
                <pre
                  className="bg-light p-2 rounded"
                  style={{
                    maxHeight: 300,
                    overflow: "auto",
                    fontSize: "0.85em",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {formatBody(selectedLog.requestBody)}
                </pre>
              </dd>

              {selectedLog.method === "DELETE" && (
                <>
                  <dt className="col-12 mt-3">Información eliminada</dt>
                  <dd className="col-12">
                    {(() => {
                      const groups = parseDeletedData(selectedLog.deletedData);
                      if (!groups || groups.length === 0) {
                        return (
                          <em className="text-muted">
                            (no se capturó información de los registros
                            eliminados)
                          </em>
                        );
                      }
                      return groups.map((group, gi) => (
                        <div key={gi} className="mb-3">
                          <div className="fw-semibold mb-1">
                            Tabla:{" "}
                            <code>{group.table}</code>{" "}
                            <Badge bg="secondary">
                              {group.rowsAffected ??
                                (group.rows?.length || 0)}{" "}
                              registro(s)
                            </Badge>
                          </div>
                          {Array.isArray(group.rows) &&
                            group.rows.map((row, ri) => (
                              <div
                                key={ri}
                                className="bg-light p-3 rounded mb-2"
                              >
                                {Object.entries(row).map(([key, value]) => (
                                  <div
                                    key={key}
                                    className="d-flex border-bottom py-1"
                                    style={{ fontSize: "0.9em" }}
                                  >
                                    <div
                                      className="fw-semibold text-capitalize pe-2"
                                      style={{
                                        flex: "0 0 35%",
                                        maxWidth: "35%",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {String(key).replace(/_/g, " ")}
                                    </div>
                                    <div
                                      style={{
                                        flex: "1 1 65%",
                                        wordBreak: "break-word",
                                        whiteSpace: "pre-wrap",
                                      }}
                                    >
                                      {formatValue(value)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                        </div>
                      ));
                    })()}
                  </dd>
                </>
              )}
            </dl>
          </div>
        )}
      </Modal>
    </main>
  );
}

export default ListAuditLogs;
